'use client';
import { ParsedUrlQuery } from 'querystring';

import React, { useState, useEffect, useMemo } from 'react';

import { usePathname, useSearchParams } from 'next/navigation';
import Cookies from 'js-cookie';

import { SearchViewModel } from '@api/v3';
import { ListingSearchRoomInterface } from '@components/ui/search/SearchGuest/ListingSearchGuestInterface';
import { useSearchURL } from '@api/data-loaders/resource';
import {
  convertDateToMomentObject,
  DateType,
  HOTEL_SEARCH_DATE_FORMAT,
} from '@utils/dateUtil';
import { paramsToObject } from '@components/views/utils';
import { getRoomsFromCookie } from '@components/views/landing/components/bookerNext/Booker';

import { createGenericContext } from '../generateContext';

type ContextInterface = {
  location: SearchViewModel;
  setLocation: React.Dispatch<React.SetStateAction<SearchViewModel>>;
  activeLocation: SearchViewModel;
  setActiveLocation: React.Dispatch<React.SetStateAction<SearchViewModel>>;
  startDate: DateType | null;
  endDate: DateType | null;
  packageStartDate: DateType | null;
  packageEndDate: DateType | null;
  setDates: React.Dispatch<
    React.SetStateAction<{
      startDate: DateType | null;
      endDate: DateType | null;
    }>
  >;
  hotelRoomsList: Array<ListingSearchRoomInterface>;
  setHotelRoomsList: React.Dispatch<
    React.SetStateAction<ListingSearchRoomInterface[]>
  >;
  isHotelDateSelected: boolean;
  setHotelDateSelection: React.Dispatch<React.SetStateAction<boolean>>;

  isStickySearch: boolean;
  setIsStickySearch: React.Dispatch<React.SetStateAction<boolean>>;

  isLocationEntered: boolean;
  setIsLocationEntered: React.Dispatch<React.SetStateAction<boolean>>;
};

const [useSearchBarContext, SearchBarContextProvider] =
  createGenericContext<ContextInterface>();

const useSearchBarUtils = (
  initialLocation = {
    id: 0,
    text: '',
    url: '',
  }
) => {
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const pathname = usePathname();
  const [hotelRoomsList, setHotelRoomsList] = useState(getRoomsFromCookie);

  const [location, setLocation] = useState<SearchViewModel>(initialLocation);

  const [activeLocation, setActiveLocation] =
    useState<SearchViewModel>(initialLocation);

  const [{ startDate, endDate }, setDates] = useState<{
    startDate: DateType | null;
    endDate: DateType | null;
  }>({ startDate: null, endDate: null });

  const [{ defaultStartDate, defaultEndDate }, setDefaultDates] = useState<{
    defaultStartDate: DateType | null;
    defaultEndDate: DateType | null;
  }>({ defaultStartDate: null, defaultEndDate: null });
  const [isHotelDateSelected, setHotelDateSelection] = useState<boolean>(false);

  const [isStickySearch, setIsStickySearch] = useState<boolean>(false);

  const [isLocationEntered, setIsLocationEntered] = useState<boolean>(true);

  useEffect(() => {
    const newStartDate = query.in
      ? convertDateToMomentObject(query.in as string, HOTEL_SEARCH_DATE_FORMAT)
      : null;
    const newEndDate = query.out
      ? convertDateToMomentObject(query.out as string, HOTEL_SEARCH_DATE_FORMAT)
      : null;

    setDates((prevDates) => {
      if (
        prevDates.startDate !== newStartDate ||
        prevDates.endDate !== newEndDate
      ) {
        return { startDate: newStartDate, endDate: newEndDate };
      } else return prevDates;
    });
  }, [query]);

  useEffect(() => {
    const localStartDate = Cookies.get('startDate');
    const localEndDate = Cookies.get('endDate');

    // Çerezler boşsa, varsayılan tarihleri kullan
    const validStartDate = localStartDate
      ? convertDateToMomentObject(localStartDate, HOTEL_SEARCH_DATE_FORMAT)
      : null;
    const validEndDate = localEndDate
      ? convertDateToMomentObject(localEndDate, HOTEL_SEARCH_DATE_FORMAT)
      : null;

    setDefaultDates({
      defaultStartDate: validStartDate,
      defaultEndDate: validEndDate,
    });
  }, [pathname, searchParams]);
  return {
    hotelRoomsList,
    setHotelRoomsList,
    location,
    setLocation,
    startDate: startDate ?? defaultStartDate ?? null,
    endDate: endDate ?? defaultEndDate ?? null,
    packageStartDate: startDate,
    packageEndDate: endDate,
    setDates,
    isHotelDateSelected,
    setHotelDateSelection,
    activeLocation,
    setActiveLocation,
    isStickySearch,
    setIsStickySearch,
    isLocationEntered,
    setIsLocationEntered,
  };
};

const SearchBarProvider = (props: React.PropsWithChildren<unknown>) => {
  const value = useSearchBarUtils();

  return (
    <SearchBarContextProvider value={value}>
      {props.children}
    </SearchBarContextProvider>
  );
};

const SSrSearchBarProvider = (props: React.PropsWithChildren<unknown>) => {
  const initialLocation = useSearchURL();

  const value = useSearchBarUtils({ ...initialLocation, url: '' });

  return (
    <SearchBarContextProvider value={value}>
      {props.children}
    </SearchBarContextProvider>
  );
};

export { useSearchBarContext, SearchBarProvider, SSrSearchBarProvider };
